/**
 * Session Storage Manager
 * Centralized management of preparation data in sessionStorage
 */

import { PreparationSessionData } from "@/types/meeting-preparation.types";
import { logger } from "@/utils/logger"
const STORAGE_KEY = "meeting_preparation";

/**
 * Save preparation data to sessionStorage
 */
export const savePreparationData = (data: PreparationSessionData): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    logger.error(
      "[sessionStorageManager] Failed to save preparation data:",
      error
    );
  }
};

/**
 * Load preparation data from sessionStorage
 */
export const loadPreparationData = (): PreparationSessionData | null => {
  try {
    const data = sessionStorage.getItem(STORAGE_KEY);
    if (!data) return null;
    return JSON.parse(data) as PreparationSessionData;
  } catch (error) {
    logger.error(
      "[sessionStorageManager] Failed to load preparation data:",
      error
    );
    return null;
  }
};

/**
 * Clear preparation data from sessionStorage
 */
export const clearPreparationData = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.error(
      "[sessionStorageManager] Failed to clear preparation data:",
      error
    );
  }
};

/**
 * Check if preparation is complete (all questions answered with minimum length)
 */
export const isPreparationComplete = (
  answers: { [complementId: string]: string },
  totalQuestions: number,
  minLength: number = 30
): boolean => {
  const answeredCount = Object.values(answers).filter(
    (answer) => answer.trim().length >= minLength
  ).length;
  return answeredCount === totalQuestions;
};

/**
 * Get completion percentage
 */
export const getCompletionPercentage = (
  answers: { [complementId: string]: string },
  totalQuestions: number
): number => {
  if (totalQuestions === 0) return 0;
  const answeredCount = Object.values(answers).filter(
    (answer) => answer.trim().length > 0
  ).length;
  return Math.round((answeredCount / totalQuestions) * 100);
};

/**
 * Validate single answer
 */
export const validateAnswer = (
  answer: string,
  minLength: number = 30
): boolean => {
  return answer.trim().length >= minLength;
};
